import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import ValidateFieldWrap, { getFieldValidation } from "./ValidateFieldWrap";
import {
    Sticky,
    Dialog,
    DialogType,
    DialogFooter,
    DefaultButton,
    PrimaryButton,
    IColumn,
    Callout,
    Checkbox,
    HoverCard,
    TextField,
    DetailsRow,
    IconButton,
    DetailsList,
    TooltipHost,
    HoverCardType,
    ConstrainMode,
    DetailsHeader,
    ContextualMenu,
    ScrollablePane,
    DirectionalHint,
    IDetailsRowProps,
    StickyPositionType,
    IDetailsHeaderProps,
    ScrollbarVisibility,
    IContextualMenuProps,
    DetailsListLayoutMode
} from "@fluentui/react";
import {
    gextrprop,
    extrlevels,
    gloggedinuser,
    gselectedgroup,
    ginvoicetablesort,
    reco_type_choices
} from "../../interfaces";
import {
    optVisFields,
    doArrPropsEq,
    calcDiscount,
    allowedGeneric,
    doArrPropExist,
    invPOTableColOrd,
    numFieldsOnlyNum,
    invTotalRowColumns,
    getInvTableTotalRow,
    invDetailListStyles,
    noVerticalPasteFields,
    tooltipInHeaderStyles,
    invDetRowTotalsStyles,
    copyToClipboardInvoice,
    invItemsTextFieldStyles,
    invTotalsTextFieldStyles,
    invoiceItemsTableColumns,
    invDetailListTotalStyles,
    invItemsWithCitationLink,
    invItemsTableHeaderTooltips,
    invDetailsHeaderCustomStyles,
    invItemsMainSaveButtonStyles,
} from "../../inv_lsts";
import { setLocalFieldDefaults } from "./localFieldDefaults";
import smoothScrollIntoView from "smooth-scroll-into-view-if-needed";

import RecoCellStayOnHov from "./RecoCellStayOnHov";
import ApproveHoldButton from "../ApproveHoldButton/ApproveHoldButton";
import { tooltipHtmlBasic, tooltipHtmlArray } from "../detailedList/detailedMdls";
import { DataVisualizationCallout } from "../../components/DataVisualization/DataVisualizationCallout";
import { shortRecoStatus, getQuantityToUse, generateInvRowStyles, isTotToTotMssmatched, sortInvItemTableNested, applyColumnOrder, parseFlags, hasLinkedPurchaseOrders, mergeItemsByLinkedPo } from "./InvoiceItemsUtil";
import ButtonHoldCircular from "../ButtonHoldCircular/ButtonHoldCircular";
import styles from "./InvoiceItemsTable.module.css";
import {fieldSelectStyles, invOvrvuHoverStyles, invItemListIsPercCheckboxStyle, selCheckboxStyles} from "../../styles_glob";
import { getNestVal, getLocInvItemCols, setLocInvItemCols, setLocInvColumnOrder, getLocInvColumnOrder, getProp, getValidationResult, notEmpty, not_empty_prop, reorderByItemNumber, reorderSelectionByItemNumber } from "../../util_glob";
import PoEditCellV1 from "./PoEditV1/PoEditCellV1";
import { isPoEditingEnabled_po_ed_v1, getItemMultiAssignPos_po_ed_v1, MULTI_ASSIGN_KEY_po_ed_v1 } from "./PoEditV1/poEditV1";
import { PiColumnsPlusLeftFill } from "react-icons/pi";


import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

import { toast } from "react-toastify";
import { FaSortDown } from "react-icons/fa";
import { GrAggregate } from "react-icons/gr";
import { FaRegLightbulb } from "react-icons/fa";
import { VscDiffModified, VscCombine } from "react-icons/vsc";
import { LuSave, LuFileJson } from "react-icons/lu";
import { BiSolidUpArrowSquare } from "react-icons/bi";
import { FaSortUp, FaRegCopy, FaCircleCheck } from "react-icons/fa6";
import { MdAddCircleOutline, MdViewColumn } from "react-icons/md";
import { FaLock, FaLockOpen } from "react-icons/fa";
import { FcDeleteRow } from "react-icons/fc";
import { MdDragIndicator } from "react-icons/md";
import Fireflies from "../Fireflies/Fireflies";
import { FaCodeCompare } from "react-icons/fa6";

// case-insensitive match of a term against text; supports wildcard (*) or plain substring (indexOf)
const matchesColumnSearch = (text: string, term: string): boolean => {
    if (term.length < 3) return false;
    const hay = text.toLowerCase();
    const needle = term.toLowerCase();
    if (needle.includes('*')) {
        const pattern = needle.split('*').map(s => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*');
        try { return new RegExp(pattern).test(hay); } catch { return false; }
    }
    return hay.indexOf(needle) !== -1;
};

// Sortable/draggable row in the column settings slideout - drag handle + visibility toggle (no sorting)
interface SortableColumnItemProps {
    column: string;
    label: string;
    isVisible: boolean;
    isMatch: boolean;
    onToggleVisible: () => void;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({ column, label, isVisible, isMatch, onToggleVisible }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1
    };
    return (
        <div ref={setNodeRef} style={style} data-colkey={column} className={`${styles.sortableColumnItem} ${isMatch ? styles.sortableColumnItemMatch : ''}`}>
            <span className={styles.dragHandle} {...attributes} {...listeners}>
                <MdDragIndicator size={16} color="#888" />
            </span>
            <Checkbox
                label={label}
                checked={isVisible}
                onChange={onToggleVisible}
                styles={fieldSelectStyles}
                className={styles.sortableColumnCheckbox}
            />
        </div>
    );
};

interface InvoiceItemsTableProps {
    jsonString: string | any;
    loggedInUser: gloggedinuser;
    fid: string;
    selectedGroup: gselectedgroup;
    listRefreshRequest?: () => void;
    onDataHighlight?: (searchString: any) => void;
    onParagraphHighlight?: (searchString: any) => void;
    sendAndSaveInvItems?: (gridData: any, fid: string, approve?: boolean) => void; // save data
    invItemsCellUpdCallback: (invItems: any[], fid: string, totalRowArr: any[] ) => void;
    approved?: boolean;
}


const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({ jsonString, loggedInUser, fid, selectedGroup, listRefreshRequest, onDataHighlight, onParagraphHighlight, sendAndSaveInvItems, invItemsCellUpdCallback, approved }) => {
    /* Invoice Line Items Table + PO Matching & More
    * Grid Data AI Tool Source:
    * LINK app/backend/kgnf_ai_instr/inv.py#INV_VERIFY_TOOL
    *
    * Grid Data Example:
    * LINK app/backend/model_exmpls/reco_ai_fin_res.json
    */

    const [fidState, setFidState] = useState<string>(fid);

    const [gridData, setGridData] = useState(typeof jsonString == 'string' ? JSON.parse(jsonString) : jsonString); // items holding updates. updates on json_str_update, edit, add, delete, sort, column drop
    const [invItems, setInvItems] = useState<any[]>([]);        // tracks updates. holds gridData?.invoice_items
    const [invItemsOg, setInvItemsOg] = useState<any[]>([]);    // version as on load for user modification flagging. holds gridData?.invoice_items
    const [selectedRows, setSelectedRows] = useState<boolean[]>([]); // parallel boolean array index-aligned with invItems
    const [invItemTotalRow, setInvItemTotalRow]  = useState<any[]>([]);

    const [isSorted, setIsSorted] = useState<ginvoicetablesort | null>(null);
    const [hasUpdates, setHasUpdates] = useState<boolean>(false);
    const [isPoReviewDialogOpen, setIsPoReviewDialogOpen] = useState<boolean>(false);
    const [invColumns, setInvColumns] = useState<IColumn[]>([]);
    const [invItemTotalCols, setInvItemTotalCols]  = useState<IColumn[]>([]);
    const [removingKey, setRemovingKey] = useState<string | null>(null);

    const [invoiceState, setInvoiceState] = useState<string>('');
    const [isShiftPressed, setIsShiftPressed] = useState<boolean>(false);
    const [isCtrlPressed, setIsCtrlPressed] = useState<boolean>(false);

    // column visibility state
    const [isColumnCalloutVisible, setIsColumnCalloutVisible] = useState<boolean>(false);
    const [columnSearch, setColumnSearch] = useState<string>(''); // slideout field-name search (highlights matches after 3rd char)
    const columnSettingsListRef = useRef<HTMLDivElement>(null);   // scroller for the column chooser rows (scroll-to-match)
    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set(getLocInvItemCols(selectedGroup.selectionId, 'invoice_items')));
    const [columnChoiceList, setColumnChoiceList] = useState<{ key: string; name: string }[]>(optVisFields); // exclude delete col from choice list

    // column reordering state (reorder is done via the column settings slideout)
    const [columnOrder, setColumnOrder] = useState<string[]>([]);

    // column header context menu state
    const [contextMenuProps, setContextMenuProps] = useState<IContextualMenuProps | undefined>(undefined);
    const [lockLinePrice, setLockLinePrice] = useState<boolean>(true);

    const [recoClean, setRecoClean] = useState<any[]>([]);
    const [recoDynamicCols, setRecoDynamicCols] = useState<string[]>([]);
    const [altModeId, setAltModeId] = useState<string>('normal');

    const dragColumnRef = useRef<string | null>(null);
    const draggedOverColumnRef = useRef<string | null>(null);
    const columnCalloutButtonRef = useRef<any>(null);
    const lastClickedSelectionIndex = useRef<number | null>(null);

    const getPrecalcTotalConstant = useCallback(() => {
        return gridData?.['calculated_invoice_items_total'] ?? 0
    }, [gridData, invItems, invItemsOg]);


    // fid ue
    useEffect(() => {
        if (fidState !== fid) {
            setHasUpdates(false)
            setFidState(fid);
        }
    }, [fid]);

    useEffect(() => {
        setIsPoReviewDialogOpen(false);
    }, [fid, selectedGroup.selectionId]);


    useEffect(() => {
        if (jsonString) {
            const parsedData = typeof jsonString == 'string' ? JSON.parse(jsonString) : jsonString;
            setGridData(parsedData || {});
        }
    }, [jsonString]);


    // shift and ctrl keys
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Alt') {
                event.preventDefault(); // stop Alt from moving focus to the browser menu bar
                setIsShiftPressed(true);
                setAltModeId('alt');
            }
            if (event.key === 'Control') {
                setIsCtrlPressed(true);
                setAltModeId('ctrl');
            }
        };
        const handleKeyUp = (event: KeyboardEvent) => {
            if (event.key === 'Alt') {
                setIsShiftPressed(false);
                setAltModeId('normal');
            }
            if (event.key === 'Control') {
                setIsCtrlPressed(false);
                setAltModeId('normal');
            }
        };
        // reset modifier state if the pointer leaves the viewport while a key is held
        const handleMouseLeave = () => {
            setIsShiftPressed(false);
            setIsCtrlPressed(false);
            setAltModeId('');
        };
        // re-sync modifier state from the mouse event when the pointer returns,
        // since the window may not receive keydown events until it regains focus
        const handleMouseEnter = (event: MouseEvent) => {
            setIsShiftPressed(event.altKey);
            setIsCtrlPressed(event.ctrlKey);
            setAltModeId(event.ctrlKey ? 'ctrl' : event.altKey ? 'alt' : 'normal');
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        document.addEventListener('mouseleave', handleMouseLeave);
        document.addEventListener('mouseenter', handleMouseEnter);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('mouseleave', handleMouseLeave);
            document.removeEventListener('mouseenter', handleMouseEnter);
        };
    }, []);

    // selection derived state
    const selectedCount = selectedRows.reduce((acc, v) => acc + (v ? 1 : 0), 0);
    const allSelected   = invItems.length > 0 && selectedCount === invItems.length;
    const someSelected  = selectedCount > 0 && !allSelected;

    // add del col in main table; prepend selection col (outside applyColumnOrder/hiddenColumns pipeline)
    const selectColumn: IColumn = {
        key: '__select', name: '', fieldName: '__select', minWidth: 28, maxWidth: 28, isResizable: false,
        onRender: (_item: any, index?: number) => (
            <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={index !== undefined ? !!selectedRows[index] : false}
                    styles={selCheckboxStyles}
                    onChange={(ev: any, checked) => {
                        if (index === undefined) return;
                        const isShift = !!ev?.nativeEvent?.shiftKey;
                        setSelectedRows(prev => {
                            const next = prev.slice();
                            while (next.length < invItems.length) next.push(false);
                            // shift-click: set every row between last-clicked and current to the new checked state
                            if (isShift && lastClickedSelectionIndex.current !== null && lastClickedSelectionIndex.current !== index) {
                                const start = Math.min(lastClickedSelectionIndex.current, index);
                                const end   = Math.max(lastClickedSelectionIndex.current, index);
                                for (let i = start; i <= end; i++) next[i] = !!checked;
                            } else {
                                next[index] = !!checked;
                            }
                            return next;
                        });
                        lastClickedSelectionIndex.current = index;
                    }}
                />
            </div>
        )
    };

    // 🔗 PO editing (v1) — applies updated invoicedata returned by the backend after a PO change
    const onPoEditV1Applied = useCallback((updatedInvoiceData: any) => {
        if (!updatedInvoiceData || typeof updatedInvoiceData !== 'object') return;
        setGridData(updatedInvoiceData);
        const updatedItems = updatedInvoiceData.invoice_items || [];
        setInvItems(updatedItems);
        setInvItemsOg(JSON.parse(JSON.stringify(updatedItems)));
    }, [fidState]);

    // 🔗 PO editing (v1) — dropdown column, injected only when enabled for the space
    const selectedInvItems = invItems.filter((_, i) => selectedRows[i]); // ticked lines; passed to each cell for PO broadcast
    const poEditV1Column: IColumn = {
        key: 'po_ed_v1',
        name: 'PO',
        fieldName: 'po_ed_v1',
        minWidth: 90,
        currentWidth: 90,
        maxWidth: 160,
        isResizable: true,
        isPadded: false,
        onRender: (item: any) => (
            <PoEditCellV1
                item={item}
                fid={fidState}
                selectedGroup={selectedGroup}
                loggedInUser={loggedInUser}
                onApplied={onPoEditV1Applied}
                contractorVat={gridData?.contractor_vat_identification || ""}
                purchase_order={gridData?.purchase_order || ""}
                selectedItems={selectedInvItems}
            />
        )
    };

    const columnsWithDelete = [
        selectColumn,
        ...(isPoEditingEnabled_po_ed_v1(selectedGroup) ? [poEditV1Column] : []),
        ...applyColumnOrder(invColumns.filter(col => !hiddenColumns.has(col.key)), columnOrder)
    ];

    // keeping parent aware of unsaved user changes
    useEffect(() => {
        let totRow = getInvTableTotalRow(invItems, getPrecalcTotalConstant());
        invItemsCellUpdCallback(invItems, fidState, totRow);
        setInvItemTotalRow(totRow);
        setInvItemTotalCols(invTotalRowColumns(renderTotalsCell));
    }, [invItems, invItemsOg]);

    // on new json string set inv items as loaded state
    useEffect(() => {
        try{
            import.meta.env.DEV && console.log("‹linking‾inv‾items›", gridData);

            (gridData?.['purchase_order_info'] || {})?.matched_order_items?.forEach((mi: any) => {
                // a catch-all PO line is duplicated once per invoice line ⇒ link by the row's own invoice line first
                // (inv_item_num / ai_recon.invoice_item_number ↔ item_mapping_number, else item_number), then by PO line id.
                // A PO line may be the primary reconciliation OR one of the extra multi-assigned lines.
                const ownInvNum = String(mi?.ai_recon?.invoice_item_number ?? (Array.isArray(mi?.inv_item_num) ? '' : mi?.inv_item_num ?? '')).trim();
                let invItem = (ownInvNum && ownInvNum !== '0')
                    ? gridData?.invoice_items?.find((ii: any) => String(ii?.item_mapping_number ?? ii?.item_number ?? '').trim() === ownInvNum)
                    : undefined;
                if (!invItem) invItem = gridData?.invoice_items?.find((ii: any) => ii?.reconciliation?.id == mi.id || getItemMultiAssignPos_po_ed_v1(ii).some((ex: any) => ex?.id == mi.id));
                mi['invoice_desc']          = invItem?.description || '';
                mi['invoice_unit_price']    = invItem?.unit_price ?? '';
            });
            if (gridData?.validation_usage) delete gridData.validation_usage;   // incomplete validation usage, remove to not confuse
            if (gridData?.premain_data) {
                gridData.premain_data.forEach((pd: any) => {
                    if (pd?.usage) delete pd.usage; // incomplete preparatory usage, remove to not confuse
                });
            }
        } catch (error) {
            import.meta.env.DEV && console.log("‹errr‾linking‾inv‾items›", error);
        }

        // ANCHOR[id=setRecoClean] set recoClean for DataVisualizationCallout
        setRecoClean(gridData?.['purchase_order_info']);

        if (gridData?.invoice_items?.length > 0) {
            // Ensure Discount/Charge rows have quantity & calculated_total_price for client-side display
            for (const it of gridData.invoice_items) {
                if (it.item_type === 'Discount/Charge') {
                    if (!it.quantity) it.quantity = 1;
                    if (not_empty_prop(it, 'calculated_total_price') == false) it.calculated_total_price = it.discount ?? 0;
                }
            }
            setInvItems(gridData.invoice_items);
            if (invItemsOg.length === 0 || fidState !== fid) {
                setInvItemsOg(JSON.parse(JSON.stringify(gridData.invoice_items))); // set og items only if not already set (first load or fid change)
                setSelectedRows(new Array(gridData.invoice_items.length).fill(false)); // reset selection on load/fid change
            } else {
                // pad/trim selection to match items length without disturbing existing selections
                setSelectedRows(prev => {
                    const target = gridData.invoice_items.length;
                    if (prev.length === target) return prev;
                    const next = prev.slice(0, target);
                    while (next.length < target) next.push(false);
                    return next;
                });
            }
        }
    }, [gridData.invoice_items]);

    const isThereCompoundQuantity: boolean = useMemo(() => { // check if there are compound quantities in any items
        return doArrPropsEq(gridData?.invoice_items, 'quantity', 'compound_quantity')
    }, [gridData]);

    // other_xx fields existence checks
    const isThereOther1: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'other_01')
    }, [gridData]);

    const isThereOther2: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'other_02')
    }, [gridData]);

    const isThereOther3: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'other_03')
    }, [gridData]);

    const isThereOther4: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'other_04')
    }, [gridData]);

    const isThereItemVat: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'item_vat')
    }, [gridData]);

    const isThereReco: boolean = useMemo(() => {
        return doArrPropExist(gridData?.invoice_items, 'reconciliation')
    }, [gridData]);


    const isThereOverview: boolean = useMemo(() => {
        return gridData?.your_view ? true : false;
    }, [gridData]);

    // check if discounts in any items
    const recoType: reco_type_choices = useMemo(() => {
        return gridData?.reco_type || '';
    }, [gridData]);

    const getDiscount = (item: any): number => {
        const tot_no_disco = getProp(item, 'unit_price', 0) * getQuantityToUse(item); // get original price › discount amount + unit price
        const disco        = getProp(item, 'discount', 0);
        const isPerc       = getProp(item, 'discount_is_perc', false)
        return calcDiscount(tot_no_disco, disco, isPerc); // get discount calculated over non-discounted price
    }

    // in middle of user modifications
    const isCellModified = useCallback((index: number | undefined, fieldName: string): boolean => {
        if (index === undefined) return false;
        if (fieldName == 'calculated_total_price') {
            if (String(invItemsOg[index]?.[fieldName] ?? '') == '') return false
            return String(invItems[index]?.[fieldName] ?? '') !== String(invItemsOg[index]?.[fieldName] ?? '');
        }
        if (!invItemsOg[index]) return false;

        // compare as string for robustness
        return String(invItems[index]?.[fieldName] ?? '').replace(/(\.\d*?[1-9])0+$/, "$1") != String(invItemsOg[index]?.[fieldName] ?? '').replace(/(\.\d*?[1-9])0+$/, "$1");
    }, [invItems, invItemsOg]);

    const showConditionalEditIcon = (modified: boolean)  => {
        return modified ? <div className={styles.cellModIconFlx}><div className={styles.cellModIconInternal}/><div className={styles.cellModIconWrap}><VscDiffModified size={15} color="#00e5ff" /></div></div> : null;
    }

    // vertical paste: distributes pasted lines across subsequent rows in the same column
    const handleVerticalPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>, index: number | undefined, fieldName: string) => {
        if (index === undefined || noVerticalPasteFields.has(fieldName)) return;

        const pastedText = event.clipboardData.getData('text');
        const lines = pastedText.split(/\r?\n/);
        if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

        if (lines.length <= 1) return; // single line, let default paste handle it

        event.preventDefault();

        for (let i = 0; i < lines.length; i++) {
            const targetIndex = index + i;
            if (targetIndex >= invItems.length) break;
            handleCellChange(targetIndex, fieldName, lines[i].trim());
        }
    }, [invItems]);

    // citation click ⇒ prefer ParagraphOverlay highlighting when available, else legacy regex highlight
    const handleCitationClick = useCallback((value: any) => {
        if (onParagraphHighlight) onParagraphHighlight(String(value));
        else if (onDataHighlight) onDataHighlight(String(value));
    }, [onParagraphHighlight, onDataHighlight]);
    const hasCitationHandler = !!(onParagraphHighlight || onDataHighlight);

    // render editable cell ----
    // ANCHOR[id=renderEditableCell]
    const renderEditableCell = useCallback((item: any, index?: number, column?: IColumn) => {
        const fieldName = column?.fieldName || '';
        const restrictNumberInput = numFieldsOnlyNum.includes(fieldName); // restrict input for specific fields

        const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (!restrictNumberInput) return;
            if (!allowedGeneric.test(event.key)) event.preventDefault();
        };
        const modified = isCellModified(index, fieldName);
        return (
            <div className={styles.showInCitationLink}>
                {/* citation link */}
                {invItemsWithCitationLink.includes(fieldName) && (
                    <div className={styles.invTableCitationLink} onClick={hasCitationHandler ? () => handleCitationClick(item[fieldName]) : undefined}>
                        <BiSolidUpArrowSquare size={15}/>
                    </div>
                )}
                {showConditionalEditIcon(modified)}
                {/* actual field */}
                <ValidateFieldWrap validation={getFieldValidation(item, fieldName)}>
                    <TextField
                        value={item[fieldName] ?? ''}
                        onChange={(e, newValue) => handleCellChange(index, fieldName, newValue)}
                        onPaste={(e) => handleVerticalPaste(e, index, fieldName)}
                        styles={invItemsTextFieldStyles}
                        className={modified ? styles.modifiedCell : undefined}
                        onKeyPress={restrictNumberInput ? handleKeyPress : undefined}
                    />
                </ValidateFieldWrap>
            </div>
        );
    }, [invItems, invItemsOg]);

    // render toggle cell
    const renderTogglebleCell = useCallback(
        (item: any, index?: number, column?: IColumn) => {
            const fieldName = column?.fieldName || '';
            const modified = isCellModified(index, fieldName);
            return (
                <div className={styles.showInCitationLink}>
                    {/* citation link */}
                    {invItemsWithCitationLink.includes(fieldName) && (
                        <div className={styles.invTableCitationLink} onClick={hasCitationHandler ? () => handleCitationClick(item[fieldName]) : undefined}>
                            <BiSolidUpArrowSquare size={15}></BiSolidUpArrowSquare>
                        </div>
                    )}
                    {/* actual field */}
                    <Checkbox
                        className={styles.itemListIsPPerCheckBox + (modified ? ' ' + styles.modifiedCell : '')}
                        styles={invItemListIsPercCheckboxStyle}
                        disabled={false}
                        checked={item[fieldName] === 'true' || item[fieldName] === true}
                        onChange={(e, newValue) => handleCellChange(index, fieldName, newValue === true ? 'true' : 'false')}
                    />
                </div>
            );
        },
        [invItems, invItemsOg]
    );


    const renderDisabledCell = useCallback(
        (item: any, index?: number, column?: IColumn) => {
            const fieldName = column?.fieldName || '';
            const modified = isCellModified(index, fieldName);
            return (
                <TextField
                    value={item[fieldName] || ''}
                    onChange={(e, newValue) => handleCellChange(index, fieldName, newValue)}
                    styles={invItemsTextFieldStyles}
                    disabled={true}
                    className={modified ? styles.modifiedCell : undefined}
                />
            );
        }, [invItems, invItemsOg]
    );


    const renderTotalsCell = useCallback(
        (item: any, index?: number, column?: IColumn) => {
            const fieldName = column?.fieldName || '';
            const modified = isCellModified(index, fieldName);
            return (
                <div className={styles.totalsText}>
                    <TooltipHost content={`${column?.name ?? ''}: ${item[fieldName] ?? ''}`} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                        <TextField value={item[fieldName] ?? ''} styles={invTotalsTextFieldStyles} disabled={true}/>
                    </TooltipHost>
                </div>
            );
        }, [invItems, invItemsOg]
    );


    const renderDisabledNestedCell = useCallback(
        (item: any, index?: number, column?: IColumn) => {
            const fieldName = column?.fieldName || '';
            return (
                <TooltipHost content={getNestVal(item, fieldName, '')} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                    <div className={styles.disabledNestedCell} >
                        <TextField
                            value={getNestVal(item, fieldName, '')}
                            onChange={(e, newValue) => handleCellChange(index, fieldName, newValue)}
                            styles={invItemsTextFieldStyles}
                            disabled={true}
                        />
                    </div>
                </TooltipHost>
            );
        },
        [invItems, invItemsOg]
    );

    // ANCHOR[id=setInvColumnsCall] Get Table Column
    useEffect(() => {
        if (gridData?.invoice_items?.length > 0) {
            setInvColumns(
                invoiceItemsTableColumns(
                    renderEditableCell,
                    renderDisabledCell,
                    renderTogglebleCell,
                    renderRecoCell,
                    renderDisabledNestedCell,
                    isThereCompoundQuantity ?? false,
                    isThereReco ?? false,
                    recoType,
                    recoDynamicCols,
                    isThereOther1 ?? false,
                    isThereOther2 ?? false,
                    isThereOther3 ?? false,
                    isThereOther4 ?? false
                )
            );
        }
    }, [invItems]);


    // load column order from localStorage on mount
    useEffect(() => {
        const savedOrder = getLocInvColumnOrder(selectedGroup.selectionId);
        if (savedOrder.length > 0) setColumnOrder(savedOrder);
    }, [selectedGroup.selectionId]);


    const getRecoDynamicCols = (parsedData: any) => {
        let reconObj = Object.assign({}, ...(parsedData?.invoice_items ?? []).map((item: any) => item?.reconciliation ?? {})); // let reconObj = parsedData?.invoice_items?.[0]?.reconciliation || {}

        // get reco object keys
        let recoColNames = Object.keys(reconObj).map((rc: any) => (`reconciliation.${rc || ''}`)).filter((recokey: string) => recokey != '' && recokey != 'reconciliation.ai_recon' && recokey != `reconciliation.${MULTI_ASSIGN_KEY_po_ed_v1}`);

        // get reco ⇒ ai_recon object keys
        let recoAiReconColNames: string[] = [];

        if (Array.isArray(reconObj?.ai_recon ?? {}) == true && reconObj?.ai_recon.length > 0){
            recoAiReconColNames = Object.keys(reconObj?.ai_recon[0] || {}).map((rc: any) => (`reconciliation.ai_recon.${rc || ''}`)).filter((recokey: string) => recokey != '');
        } else {
            recoAiReconColNames = Object.keys(reconObj?.ai_recon || {}).map((rc: any) => (`reconciliation.ai_recon.${rc || ''}`)).filter((recokey: string) => recokey != '');
        }

        // get configured item level custom props
        let customProps = (selectedGroup?.extractionfieldlist?.custom_props || []).filter((cprop: gextrprop) => cprop.level === 'item' as extrlevels).map((cprop: gextrprop) => cprop.name || '') || [];

        let recoCustomProps = Array.from(new Set(customProps))

        recoColNames        = Array.from(new Set(recoColNames));            // unique
        recoAiReconColNames = Array.from(new Set(recoAiReconColNames));     // unique

        let allRecoColNames = [...recoColNames, ...recoAiReconColNames , ...recoCustomProps];

        setColumnChoiceList(prev => {
            let updated = [...prev];
            allRecoColNames.forEach(colName => {
                if (!updated.find(uc => uc.key == colName)) updated.push({ key: colName, name: colName });
            });
            return updated;
        });

        if (hiddenColumns?.size == 1 && hiddenColumns.has('not_set')) setHiddenColumns(new Set(allRecoColNames)); // show all by default

        return allRecoColNames;
    }

    // parse json str and set data (main table data)
    useEffect(() => {
        if (jsonString) {
            try {
                const parsedData = typeof jsonString == 'string' ? JSON.parse(jsonString) : jsonString;
                setInvoiceState(parsedData?.status_message || '');
                setGridData(parsedData || {});
                let recoDynamicCols: string[] = [];
                let recoCols = getRecoDynamicCols(parsedData);
                setRecoDynamicCols(recoCols);
                setInvColumns(
                    invoiceItemsTableColumns(
                        renderEditableCell,
                        renderDisabledCell,
                        renderTogglebleCell,
                        renderRecoCell,
                        renderDisabledNestedCell,
                        isThereCompoundQuantity ?? false,
                        isThereReco ?? false,
                        recoType,
                        recoDynamicCols,
                        isThereOther1 ?? false,
                        isThereOther2 ?? false,
                        isThereOther3 ?? false,
                        isThereOther4 ?? false
                    )
                );
            } catch (error) {
                console.error("‹errr‾parse‾item_inv_json›", error);
            }
        }
    }, [jsonString]);

    // send for save upstream (from save button)
    const sendEdited = () => {
        if (sendAndSaveInvItems) sendAndSaveInvItems(gridData, fidState);
        // after save, update og items to current
        setInvItemsOg(JSON.parse(JSON.stringify(gridData.invoice_items)));
        setSelectedRows(new Array(gridData.invoice_items?.length || 0).fill(false));
    }

    const saveApprovedEdits = () => {
        if (sendAndSaveInvItems) sendAndSaveInvItems(gridData, fidState, true);
        // after save, update og items to current
        setInvItemsOg(JSON.parse(JSON.stringify(gridData.invoice_items)));
        setSelectedRows(new Array(gridData.invoice_items?.length || 0).fill(false));
    }

    // PO review must be confirmed before saving, approving, or resetting local edits.
    const approveAndSendEdited = () => {
        if (isPoEditingEnabled_po_ed_v1(selectedGroup)) {
            setIsPoReviewDialogOpen(true);
            return;
        }
        saveApprovedEdits();
    }

    // get classname for row
    const getGreenRedRowClass = (prps: IDetailsRowProps | undefined): string => {
        return isTotToTotMssmatched(prps) ? styles.invTableTotalCellMiss : styles.invTableTotalCellGood;
    }

    // cell edit on main table
    const handleCellChange = (index: number | undefined, fieldName: string, newValue: string | undefined) => {
        if (index === undefined) return;
        if (notEmpty(invItems) && invItems.length > 0) {
            // if user moding qty › if qty eq compound qty ⇒ update compound qty as well, to keep them equal (and prevent compound showing suddenly due to difference)
            if(fieldName == 'quantity') {
                if (invItems[index]?.['compound_quantity']) {
                    if(fieldName == 'quantity' && invItems[index]['quantity'] == invItems[index]['compound_quantity']){
                        invItems[index]['compound_quantity'] = newValue;
                    }
                } else {
                    if(fieldName == 'quantity') invItems[index]['compound_quantity'] = newValue;
                }
            }

            invItems[index][fieldName] = newValue; // update field that user changed

            if (lockLinePrice == true) {
                try{ // auto calc total price if not unlocked
                    let item          = invItems?.[index] || {};
                    let flags         = parseFlags(getProp(item, '_p1_flags', ''));

                    let unit_price    = getProp(item, 'unit_price', 0);
                    let qty           = getQuantityToUse(item);         // if is_compound_unit true › compound_quantity else › quantity
                    let tot_no_disco  = unit_price * qty

                    // When backend aligned prices to NET (kgnf_inv_valid_v3b), the discount is already baked into unit_price/total_price — subtracting it again would double-count, so skip the discount step for aligned rows.
                    let aligned       = getProp(item, 'line_price_aligned', false) === true;

                    let disco_amount  = 0
                    if (!aligned && getProp(item, 'discount', 0) != 0) disco_amount = getDiscount(item);

                    let tot_line      = tot_no_disco  - disco_amount
                    import.meta.env.DEV && console.log("‹tot_line›", tot_line);
                    if (getValidationResult(item) != true) {
                        import.meta.env.DEV && console.log("‹validation_failed_for_item›", item);
                    }
                    invItems[index]['total_price'] = tot_line.toFixed(2);

                } catch (error) {
                    console.error("‹errr_calc›", error);
                }
            }


            setHasUpdates(true);
            setGridData((prev: any) => ({ ...prev, invoice_items: [...invItems] }));
        }
    };

    // add new item to main table
    const handleAddItem = () => {
        if (!Array.isArray(invItems)) return;
        let newItem: { [key: string]: any; item_number?: number; is_new?: boolean } = {};
        invColumns.forEach(col => {
            newItem[col.fieldName || col.key] = "";
        });
        newItem.item_number = invItems.length + 1;
        newItem.is_new = true;
        setHasUpdates(true);
        setInvItemsOg((prev: any) => [...prev, newItem]);
        setSelectedRows(prev => [...prev, false]);
        setGridData((prev: any) => ({ ...prev, invoice_items: [...invItems, newItem] }));

    };

    // delete items from main table by indices (synchronous, used for both single + bulk)
    const deleteItemsByIndices = (indices: number[]) => {
        if (!Array.isArray(invItems) || invItems.length === 0 || indices.length === 0) return;
        const indexSet = new Set(indices);
        setHasUpdates(true);
        setInvItemsOg((prev: any) => prev.filter((_:any, i:number) => !indexSet.has(i)));
        setSelectedRows(prev => prev.filter((_, i) => !indexSet.has(i)));
        setGridData((prev: any) => ({ ...prev, invoice_items: (prev.invoice_items || []).filter((_:any, i:number) => !indexSet.has(i)) }));
    };

    // bulk delete all currently selected rows (no slide-out animation)
    const handleBulkDelete = () => {
        const indices = selectedRows.reduce<number[]>((acc, sel, i) => { if (sel) acc.push(i); return acc; }, []);
        if (indices.length === 0) return;
        deleteItemsByIndices(indices);
        toast.success(`Deleted ${indices.length} item${indices.length > 1 ? 's' : ''}`, { position: "top-center", autoClose: 2000 });
    };

    // show the merge-by-PO button only when there are invoice items linked to a PO line
    const hasLinkedPos: boolean = useMemo(() => hasLinkedPurchaseOrders(invItems), [invItems]);

    // merge all invoice items that share the same linked PO line into a single row (accountant action):
    // sums the totals following the established sum rules and uses the linked PO item description.
    const handleMergeByPo = () => {
        const { merged, mergedGroups, removed } = mergeItemsByLinkedPo(invItems);
        if (mergedGroups === 0) {
            toast.info("No PO lines with multiple matching invoice items to merge", { position: "top-center", autoClose: 2500 });
            return;
        }
        console.log('‹merged›', merged);
        setHasUpdates(true);
        setInvItems(merged);
        setInvItemsOg(JSON.parse(JSON.stringify(merged)));
        setSelectedRows(new Array(merged.length).fill(false));
        setGridData((prev: any) => ({ ...prev, invoice_items: merged }));
        toast.success(`Merged ${removed + mergedGroups} items into ${mergedGroups} PO line${mergedGroups > 1 ? 's' : ''}`, { position: "top-center", autoClose: 2500 });
    };





    // item click on main table
    const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        if (column.key === '__select') return; // selection column is not sortable
        const isDescending = isSorted?.column === column.key ? !isSorted.isSortedDescending : false;

        const sortedData   = sortInvItemTableNested(invItems, column, isDescending);
        const sortedOg     = reorderByItemNumber(sortedData, invItemsOg)
        const sortedSel    = reorderSelectionByItemNumber(sortedData, invItems, selectedRows);

        setIsSorted({ column: column.key, isSortedDescending: isDescending });
        setGridData({ ...gridData, invoice_items: sortedData });
        setInvItemsOg(sortedOg);
        setSelectedRows(sortedSel);
    };

    //  swap col vals accross cols - drag start
    const onColumnDragStart = (columnKey: string) => {
        dragColumnRef.current = columnKey;
    };

    // handle column drag over for visual feedback
    const onColumnDragOver = (columnKey: string) => {
        draggedOverColumnRef.current = columnKey;
    };

    // swap col vals accross cols (Ctrl) or copy col vals (Alt) - drag drop. Column reordering is done via the settings slideout.
    const onColumnDrop = (targetColumnKey: string) => {
        const sourceColumnKey = dragColumnRef.current;
        if (!sourceColumnKey || sourceColumnKey === targetColumnKey) {
            dragColumnRef.current = null;
            draggedOverColumnRef.current = null;
            return;
        }
        const shouldSwapOrCopyValues = isCtrlPressed || isShiftPressed;   // swap/copy column values (Ctrl or Alt pressed)
        if (shouldSwapOrCopyValues) {
            // swap or copy column values
            const updatedData = invItems.map((row: any) => {
                if (isShiftPressed) {
                    // copy mode: copy source values to target column
                    row[targetColumnKey] = row[sourceColumnKey];
                } else if (isCtrlPressed) {
                    // swap mode: swap values between columns
                    const temp = row[sourceColumnKey];
                    row[sourceColumnKey] = row[targetColumnKey];
                    row[targetColumnKey] = temp;
                }
                return row;
            });
            setHasUpdates(true);
            setGridData({ ...gridData, invoice_items: updatedData });
        }

        dragColumnRef.current = null;
        draggedOverColumnRef.current = null;
    };

    // dnd-kit sensors for reordering columns in the settings slideout
    const slideoutSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // columns shown in the settings slideout, ordered by the persisted column order.
    // toggleable = can be hidden (present in the visibility choice list); mandatory columns are always visible.
    const slideoutColumns = useMemo(() => {
        const toggleableKeys = new Set(columnChoiceList.map(c => c.key));
        return applyColumnOrder(invColumns, columnOrder).map(col => ({
            key: col.key,
            name: (col.name && col.name.trim()) ? col.name : col.key,
            toggleable: toggleableKeys.has(col.key)
        }));
    }, [invColumns, columnOrder, columnChoiceList]);

    // first column matching the field-name search (used to scroll it into view)
    const firstMatchColKey = useMemo(() => {
        if (columnSearch.length < 3) return null;
        const hit = slideoutColumns.find(c => matchesColumnSearch(c.name, columnSearch) || matchesColumnSearch(c.key, columnSearch));
        return hit ? hit.key : null;
    }, [slideoutColumns, columnSearch]);

    // scroll the first matching column into view (centered) as the search term changes
    useEffect(() => {
        if (!firstMatchColKey || !columnSettingsListRef.current) return;
        const esc = window.CSS?.escape ? window.CSS.escape(firstMatchColKey) : firstMatchColKey.replace(/"/g, '\\"');
        const row = columnSettingsListRef.current.querySelector<HTMLElement>(`[data-colkey="${esc}"]`);
        if (row) {
            const id = window.setTimeout(() => row.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
            return () => window.clearTimeout(id);
        }
    }, [firstMatchColKey, columnSearch]);

    // persist new column order on slideout drag end
    const onSlideoutColumnDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const currentOrder = slideoutColumns.map(col => col.key);
        const oldIndex = currentOrder.indexOf(String(active.id));
        const newIndex = currentOrder.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;

        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        setColumnOrder(newOrder);
        setLocInvColumnOrder(selectedGroup.selectionId, newOrder);
    };

    // table header icons instead of names for some cols
    const iconHeaders = (column: IColumn | undefined) => {
        switch (column?.key || "") {
            case "quantity":
                return (
                    <div className={styles.flatHeaderName} title="Quantity">
                        <div className={styles.headerTextWrapped}>Qty.</div>
                    </div>
                );
            case "item_measure":
                return (
                    <div className={styles.flatHeaderName} title="Measure of Units">
                        <div className={styles.headerTextWrapped}>Msr.</div>
                    </div>
                );
            case "compound_quantity":
                return (
                    <TooltipHost content={tooltipHtmlBasic("Compound Unit Quantity", "In compound unit products this is the amount of product based on unit price. Easily calculate the non-discounted line total, by multiplying the compound quantity by the unit price.")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                        <div className={styles.flatHeaderName}>
                            <div className={styles.headerTextWrapped}><GrAggregate size={12} /></div>
                        </div>
                    </TooltipHost>
                );
            default:
                return (
                    <div className={styles.flatHeaderName}>
                        <div className={(column?.name ?? "").length < 20 ? (column?.name ?? "").length < 15 ? styles.headerTextWrapped : styles.headerTextWrappedMid : styles.headerTextWrappedSmall}>{column?.name}</div>
                    </div>
                );
        }
    };

    // sticky header for main table
    const onRenderDetailsHeader = (props: IDetailsHeaderProps, defaultRender?: (props: IDetailsHeaderProps) => JSX.Element) => {
        return (
            <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true} key={`${altModeId}`}>
                <DetailsHeader
                    {...props}
                    styles={invDetailsHeaderCustomStyles}

                    onRenderColumnHeaderTooltip={tooltipProps => {
                        // selection column header: tri-state select-all checkbox, no drag/sort/context
                        if (tooltipProps?.column?.key === '__select') {
                            return (
                                <div className={styles.invTableColumnHeaderContainer} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()}>
                                    <Checkbox
                                        checked={allSelected}
                                        styles={selCheckboxStyles}
                                        title={someSelected ? `${selectedCount} selected — click to clear` : (allSelected ? 'All selected — click to clear' : 'Select all')}
                                        onChange={(_, checked) => {
                                            const shouldSelectAll = !!checked && !someSelected;
                                            setSelectedRows(new Array(invItems.length).fill(shouldSelectAll));
                                            lastClickedSelectionIndex.current = null;
                                        }}
                                    />
                                </div>
                            );
                        }
                        // value mode: Ctrl (swap) or Alt (copy) values between columns. Column reordering is done via the settings slideout.
                        const isValueMode = isCtrlPressed || isShiftPressed;
                        const backgroundColor = isValueMode ? (isShiftPressed ? "#ffe89d" : "#a8ffb0") : "#ffffff07";
                        const cursor = isValueMode ? (isShiftPressed ? "copy" : "grab") : "default";

                        return (
                            <div
                                draggable={isValueMode}
                                onDragStart={() => tooltipProps?.column?.key && onColumnDragStart(tooltipProps.column.key)}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    tooltipProps?.column?.key && onColumnDragOver(tooltipProps.column.key);
                                }}
                                onDrop={() => tooltipProps?.column?.key && onColumnDrop(tooltipProps.column.key)}
                                onClick={(ev: any) => tooltipProps?.column?.key && onColumnClick(ev, tooltipProps.column)}
                                onContextMenu={(ev: any) => tooltipProps?.column && onColumnHeaderContextMenu(tooltipProps.column, ev)}
                                style={{cursor, backgroundColor, borderRadius: '4px'}}
                                className={styles.invTableColumnHeaderContainer}
                            >
                                <TooltipHost closeDelay={100} content={tooltipHtmlArray("Bulk Operations", invItemsTableHeaderTooltips)} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                                    <div className={styles.headerGroup}>
                                        {tooltipProps?.column?.key === 'total_price' && (
                                            lockLinePrice ? (
                                                <div className={styles.lockIcon} onClick={(e) => { setLockLinePrice(false); e.preventDefault(); e.stopPropagation(); }}><FaLock size={5} /></div>
                                            ) : (
                                                <div className={styles.unlockIcon} onClick={(e) => { setLockLinePrice(true); e.preventDefault(); e.stopPropagation(); }}><FaLockOpen size={5} /></div>
                                            )
                                        )}
                                        <div className={styles.columnHeader}>{iconHeaders(tooltipProps?.column)}</div>
                                        {isSorted?.column === tooltipProps?.column?.key && (
                                            <div>
                                                {isSorted?.isSortedDescending ? (
                                                    <div className={styles.sortIconUp}><FaSortUp size={9} /></div>
                                                ) : (
                                                    <div className={styles.sortIconDown}><FaSortDown size={9} /></div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </TooltipHost>
                            </div>
                        );
                    }}
                />
            </Sticky>
        );
    };

    // render reconciliation cell
    const renderRecoCell =(item: any, index?: number, column?: IColumn) => {
        let value = item?.[(column?.fieldName || '')] || {}
        let recon = value?.['ai_recon'] || {}
        if(Array.isArray(recon)) recon = recon[0]
        return (
            <RecoCellStayOnHov
                recon={recon}
                value={value}
                q_dif={parseFloat(recon?.['difference_in_quantity'] ?? -9876543)}
                u_dif={parseFloat(recon?.['difference_in_unit_price'] ?? -9876543)}
                reco_type={((value?.['reco_type'] ?? "") == "reconcile") ? "reconcile" : ""}
                item={item}
            />
        );
    };

    // column header context menu handler
    const onColumnHeaderContextMenu = (column?: IColumn, ev?: React.MouseEvent<HTMLElement>) => {
        import.meta.env.DEV && console.log("‹col‾ctx‾menu›", column);
        if (column?.key === '__select') { ev?.preventDefault(); return; } // no context menu for selection column
        if (column && ev) {
            ev.preventDefault();
            const columnKey = column.fieldName || column.key;
            const menuItems = [
                    {
                        key: 'copyCells',
                        text: 'Copy Cells to Clipboard',
                        onClick: () => {
                            const fieldName = column.fieldName || column.key;
                            const values = invItems.map(item => String(getNestVal(item, fieldName, '') ?? '')).join('\n');
                            navigator.clipboard.writeText(values);
                            toast.success("Column values copied to clipboard", { position: "top-center", autoClose: 2000 });
                            setContextMenuProps(undefined);
                        }
                    },
                    {
                        key: 'removeValues',
                        text: 'Remove Values',
                        onClick: () => {
                            const fieldName = column.fieldName || column.key;
                            const updatedItems = invItems.map(item => ({
                                ...item,
                                [fieldName]: ''
                            }));
                            setHasUpdates(true);
                            setGridData((prev: any) => ({ ...prev, invoice_items: updatedItems }));
                            setContextMenuProps(undefined);
                        }
                    }
                ];
            if (columnKey === 'total_price') {
                menuItems.push({
                    key: 'lockLinePrice',
                    text: lockLinePrice ? '🔒 Unlock Line Price (free input)' : '🔓 Lock Line Price to Line Sum',
                    onClick: () => {
                        setLockLinePrice(prev => !prev);
                        toast.info(lockLinePrice ? "Line price unlocked – free input" : "Line price locked to calculated sum", { position: "top-center", autoClose: 2000 });
                        setContextMenuProps(undefined);
                    }
                });
            }
            setContextMenuProps({
                items: menuItems,
                target: ev.currentTarget,
                onDismiss: () => setContextMenuProps(undefined)
            });
        }
    };

    return (
        <div className={styles.invTableComponent}>
            <Dialog
                hidden={!isPoReviewDialogOpen}
                onDismiss={() => setIsPoReviewDialogOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Review PO matching",
                    subText: "Have you reviewed the PO matching before approving and saving this invoice?"
                }}
                modalProps={{
                    isBlocking: true,
                    styles: { main: { borderRadius: 12, overflow: "hidden" } }
                }}
                maxWidth={440}
            >
                <DialogFooter>
                    <DefaultButton
                        text="No"
                        onClick={() => setIsPoReviewDialogOpen(false)}
                        styles={{ root: { borderRadius: 6 } }}
                    />
                    <PrimaryButton
                        text="Yes"
                        onClick={() => {
                            setIsPoReviewDialogOpen(false);
                            saveApprovedEdits();
                        }}
                        styles={{ root: { borderRadius: 6 } }}
                    />
                </DialogFooter>
            </Dialog>
            {/* top menu */}
            <div className={styles.invTableTopMenu}>
                <div className={styles.invTableTopMenuLeftInfoBar}>
                    <div className={styles.invTableMenuText}>Items: {gridData?.invoice_items?.length}</div>
                    <div className={styles.invTableMenuVeri}>Verification: {invoiceState} {hasUpdates ? ' (Edited)' : ''}</div>
                    {selectedGroup?.useapproval == 1 && (
                        <div className={styles.invTableMenuStatus}>Status: {approved == true ? 'Approved' : 'Not Approved'}</div>
                    )}
                </div>
                {isThereReco ? (
                    <div className={styles.recoOvrvuText}>
                        {shortRecoStatus(gridData, recoType)}
                    </div>
                ) : isThereOverview && (
                        <div  className={styles.hovrCardWrap}>
                            <HoverCard
                                type={HoverCardType.plain}
                                plainCardProps={{
                                    onRenderPlainCard: () => (
                                        <div className={styles.overviewHoverCardCompact}>
                                            <div className={styles.overviewHoverCardTitle}>
                                                <div style={{marginRight: "4px"}}>
                                                    <FaRegLightbulb size={12} color="#00c6af"/>
                                                </div>Overview
                                            </div>
                                            <div className={styles.overviewHoverCardContent}>
                                                {gridData?.your_view || ''}
                                            </div>
                                        </div>
                                    ),
                                    directionalHint: DirectionalHint.bottomCenter,
                                    gapSpace: -38,
                                    calloutProps: {isBeakVisible: true, styles: {  root: { borderRadius: "8px !important", overflow: "hidden", boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)" }}}
                                }}
                                instantOpenOnClick
                                styles={invOvrvuHoverStyles}
                                cardDismissDelay={100}
                                cardOpenDelay={100}
                            >
                                <div className={styles.reLightBulb}>
                                    <FaRegLightbulb size={16} color="#00c6af" />
                                </div>
                                <div className={styles.recoOvrvuButton}>
                                    {(gridData?.your_view || '').substring(0, 100)}
                                    {(gridData?.your_view || '').length > 100 && '...'}
                                </div>
                            </HoverCard>
                        </div>
                )}
                <div className={styles.invTableTopMenuRightButs} ref={columnCalloutButtonRef}>

                    {/* approve button */}
                    {selectedGroup?.useapproval == 1 && approved != true && (
                        <div className={styles.invTableSaveButtonApproveWrap}>
                            <div className={styles.firefliesContainer}>
                                <Fireflies count={30} scale={0.2} speed={1} />
                            </div>
                            <div className={styles.invTableSaveButtonApprove}>
                                <TooltipHost content={tooltipHtmlBasic("Approve & Save", "Hold to approve the invoice and save changes")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <ButtonHoldCircular key={String(isPoReviewDialogOpen)} confirm={approveAndSendEdited} confirmed={approved || false} />
                                    </div>
                                </TooltipHost>
                            </div>
                        </div>
                    )}

                    {/* save button */}
                    <div className={styles.invTableSaveButton}>
                        <TooltipHost content={tooltipHtmlBasic("Save", "Save changes to the invoice")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                            <IconButton onClick={sendEdited} styles={invItemsMainSaveButtonStyles}>
                                <LuSave size={24} color="#00c6af" />
                            </IconButton>
                        </TooltipHost>
                    </div>

                    <div className={styles.menuDivider} />

                    {/* add item button */}
                    <div className={styles.invTableSaveButton}>
                        <TooltipHost content={tooltipHtmlBasic("Add Line Item", "Add a new line item to the invoice")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                            <IconButton onClick={handleAddItem} styles={invItemsMainSaveButtonStyles}>
                                <MdAddCircleOutline size={26} color="#00c6af" />
                            </IconButton>
                        </TooltipHost>
                    </div>

                    {/* bulk delete button (always visible, disabled when no rows selected) */}
                    <div className={styles.invTableSaveButton}>
                        <TooltipHost content={tooltipHtmlBasic(selectedCount > 0 ? `Delete ${selectedCount} Selected` : "Delete Selected", selectedCount > 0 ? "Delete all selected line items" : "Select line items to enable")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                            <IconButton onClick={handleBulkDelete} styles={invItemsMainSaveButtonStyles} disabled={selectedCount === 0}>
                                <FcDeleteRow size={24} color={selectedCount > 0 ? "#ff3d3d" : "#7e7e7e"} style={{ opacity: selectedCount > 0 ? 1 : 0.4 }} />
                            </IconButton>
                        </TooltipHost>
                    </div>


                    <div className={styles.menuDivider} />

                    {/* merge-by-PO button (only when there are items linked to a PO line) */}
                    {hasLinkedPos && (
                        <div className={styles.invTableSaveButton}>
                            <TooltipHost content={tooltipHtmlBasic("Merge Items by PO Line", "Combine all invoice items linked to the same PO line into one row: sums the totals and uses the linked PO item description")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                                <IconButton onClick={handleMergeByPo} styles={invItemsMainSaveButtonStyles}>
                                    <VscCombine size={22} color="#00c6af" />
                                </IconButton>
                            </TooltipHost>
                        </div>
                    )}

                    <div className={styles.invTableSaveButton}>
                        <DataVisualizationCallout
                            recoClean={recoClean}
                            selectedGroup={selectedGroup}
                            title="Verify"
                            buttonText="Show Data Table"
                            columnOrder={invPOTableColOrd}
                            overviewText={gridData?.reconciliation_overview || ''}
                        />
                    </div>

                    <TooltipHost content={tooltipHtmlBasic("Show/Hide Extra Columns", "Show or hide columns in the invoice items table")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                        <IconButton onClick={() => setIsColumnCalloutVisible(!isColumnCalloutVisible)} styles={invItemsMainSaveButtonStyles}>
                            <PiColumnsPlusLeftFill size={22} color="#00c6af" />
                        </IconButton>
                    </TooltipHost>
                </div>
            </div>

            {/* column settings callout - drag to reorder + toggle visibility */}
            {isColumnCalloutVisible && (
                <Callout
                    target={columnCalloutButtonRef.current}
                    onDismiss={() => { setIsColumnCalloutVisible(false); setColumnSearch(''); }}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    isBeakVisible={false}
                    gapSpace={5}
                >
                    <div className={styles.columnSettingsSlideout}>
                        <div className={styles.columnSettingsTitle}>Columns · drag to reorder</div>
                        <div className={styles.columnSettingsSearchWrap}>
                            <TextField
                                value={columnSearch}
                                onChange={(_, v) => setColumnSearch(v || '')}
                                placeholder="Search fields (min 3 chars, * wildcard)"
                                styles={invItemsTextFieldStyles}
                                className={styles.columnSettingsSearchInput}
                            />
                            {columnSearch.length >= 3 && (
                                <div className={styles.columnSettingsSearchCount}>
                                    {slideoutColumns.filter(c => matchesColumnSearch(c.name, columnSearch) || matchesColumnSearch(c.key, columnSearch)).length} match(es)
                                </div>
                            )}
                        </div>
                        <div className={styles.columnSettingsList} ref={columnSettingsListRef}>
                            <DndContext
                                sensors={slideoutSensors}
                                collisionDetection={closestCenter}
                                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                                onDragEnd={onSlideoutColumnDragEnd}
                            >
                                <SortableContext items={slideoutColumns.map(c => c.key)} strategy={verticalListSortingStrategy}>
                                    {slideoutColumns.map(col => (
                                        <SortableColumnItem
                                            key={col.key}
                                            column={col.key}
                                            label={col.name}
                                            isMatch={columnSearch.length >= 3 && (matchesColumnSearch(col.name, columnSearch) || matchesColumnSearch(col.key, columnSearch))}
                                            isVisible={col.toggleable ? !hiddenColumns.has(col.key) : true}
                                            onToggleVisible={() => {
                                                if (!col.toggleable) return;
                                                const newHidden = new Set(hiddenColumns);
                                                if (newHidden.has(col.key)) newHidden.delete(col.key);
                                                else newHidden.add(col.key);
                                                setLocInvItemCols(selectedGroup.selectionId, newHidden, 'invoice_items');
                                                setHiddenColumns(newHidden);
                                            }}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </div>
                    </div>
                </Callout>
            )}

            {/* main table */}
            <div className={styles.invTableScrollContainer}>
                <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} >
                    <DetailsList
                        items={invItems}
                        columns={columnsWithDelete.map(col => col.key === '__select' ? col : ({ ...col, onColumnClick }))}
                        setKey="set"
                        layoutMode={DetailsListLayoutMode.justified}
                        checkboxVisibility={2}
                        selectionMode={2}
                        constrainMode={ConstrainMode.unconstrained}
                        isHeaderVisible={true}
                        onColumnHeaderContextMenu={onColumnHeaderContextMenu}
                        selectionPreservedOnEmptyClick={true}
                        onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeader(props!)}
                        onRenderRow={(prps: IDetailsRowProps | undefined): JSX.Element => {
                            const baseCls = prps?.item.item_number === removingKey
                                ? styles.delSlideOut
                                : prps?.item.item_type === 'Discount/Charge'
                                    ? styles.rowDiscountCharge
                                    : '';
                            const selCls = (prps?.itemIndex !== undefined && selectedRows[prps.itemIndex]) ? styles.rowSelected : '';
                            // total_price vs calculated_total_price mismatch reason (null when they agree, or when no known presentation flag explains the gap — see explainTotalPriceMismatch)
                            //const mismatchReason = isTotToTotMssmatched(prps) ? explainTotalPriceMismatch(prps?.item) : null;
                            return (
                                <DetailsRow
                                    {...(prps as IDetailsRowProps)}
                                    styles={generateInvRowStyles(prps)}
                                    className={[baseCls, selCls].filter(Boolean).join(' ')}
                                />
                            );
                        }}
                        styles={invDetailListStyles}
                    />
                </ScrollablePane>
            </div>

            {contextMenuProps && <ContextualMenu {...contextMenuProps} />}

            {/* table totals row */}
            <div className={styles.invTableBottomMenu}>
                {/* copy csv/json buttons — inline over the totals column at the left */}
                <div className={styles.invTableBottomLeftButs}>
                    {/* copy csv data button */}
                    <div className={styles.invTableExportButton}>
                        <TooltipHost content={tooltipHtmlBasic("Copy Line Items as CSV", "Copy the line items of the invoice as CSV")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                            <IconButton onClick={() => {copyToClipboardInvoice(gridData, 2)}} styles={invItemsMainSaveButtonStyles}>
                                <FaRegCopy size={22} color="#00c6af" />
                            </IconButton>
                        </TooltipHost>
                    </div>

                    {/* copy json data button */}
                    <div className={styles.invTableExportButton}>
                        <TooltipHost content={tooltipHtmlBasic("Copy Full Data as JSON", "Copy the full invoice data as JSON")} calloutProps={{ gapSpace: 0 }}  styles={tooltipInHeaderStyles}>
                            <IconButton styles={invItemsMainSaveButtonStyles} onClick={() => {
                                try{
                                    if (gridData?.premain_data) {
                                        if (Array.isArray(gridData.premain_data)) {
                                            for (let i = 0; i < gridData.premain_data.length; i++) {
                                                const data = gridData.premain_data[i];
                                                if (data?.sequential_pages) delete data.sequential_pages;
                                            }
                                        }
                                    }
                                } catch(err){
                                    console.error("‹errr‾copy‾json›", err);
                                }
                                navigator.clipboard.writeText(JSON.stringify(gridData, null, 2));
                                toast.success("Invoice Items JSON Copied to Clipboard", { position: "top-center", autoClose: 2000 });
                            }}>
                                <LuFileJson size={22} color="#00c6af" />
                            </IconButton>
                        </TooltipHost>
                    </div>
                </div>
                <DetailsList
                    items={invItemTotalRow}
                    columns={invItemTotalCols}
                    isHeaderVisible={false}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    styles={invDetailListTotalStyles}
                    onRenderRow={(prps: IDetailsRowProps | undefined): JSX.Element => (
                        <div className={styles.shtx}>
                            <DetailsRow
                                {...(prps as IDetailsRowProps)}
                                className={getGreenRedRowClass(prps)}
                                compact={true}
                                checkboxVisibility={2}
                                selectionMode={2}
                                styles={invDetRowTotalsStyles}
                            />
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default InvoiceItemsTable;

